"use client";

import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing } from "@/i18n/routing";
import { cn } from "@/lib/cn";
import { IconGlobe } from "@/components/ui/icons";
import { useTransition } from "react";

/**
 * Selector de idioma ES/EN. Mantiene la ruta actual y cambia sólo el prefijo de
 * locale. Presente en todas las superficies (requisito de layout).
 */
export function LocaleSwitcher({ className }: { className?: string }) {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname(); // ruta actual SIN prefijo de locale
  const [isPending, startTransition] = useTransition();

  const switchTo = (next: string) => {
    if (next === locale) return;
    startTransition(() => {
      // Reconstruye la misma ruta en el otro locale (el prefijo lo agrega el router).
      router.replace(pathname, { locale: next });
    });
  };

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-sand-300 bg-surface p-0.5",
        isPending && "opacity-60",
        className,
      )}
    >
      <IconGlobe size={15} className="ml-1.5 text-ink-muted" />
      {routing.locales.map((l) => (
        <button
          key={l}
          onClick={() => switchTo(l)}
          aria-current={l === locale ? "true" : undefined}
          className={cn(
            "rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition-colors",
            l === locale
              ? "bg-forest text-bone-50"
              : "text-ink-muted hover:text-ink-soft",
          )}
        >
          {l}
        </button>
      ))}
    </div>
  );
}
